/**
 * How large Sight Reading draws its staves on desktop: the staff-sizing bounds and aspect
 * ratios the lesson's phrase manager lays its two grand staves out with, and the line gap that
 * gives for a play area. Shared so other play surfaces can draw their staves at the same size.
 */
import { DefaultConfig, MediumConfig, MinimumConfig } from 'Phaser/GameObjects/StaffLine';
import type { SizingProps } from 'Phaser/GameObjects/StaffLine/Types';
import { optimizeStaffLineHeight } from 'Phaser/GameObjects/StaffLine/Util';

/** The narrowest and widest phrase shapes (width / height) the lesson lays out. */
export const SIGHT_READING_MIN_ASPECT_RATIO = 4.5;
export const SIGHT_READING_MAX_ASPECT_RATIO = 8;

/** Desktop staff-sizing bounds (matches the legacy two-stave layout). */
export const sightReadingDesktopSizing = (): { minProps: SizingProps; maxProps: SizingProps } => {
  const maxProps: SizingProps = {
    minPaddingX: 125,
    minPaddingY: 50,
    idealPhraseConfig: DefaultConfig({}),
    minPhraseConfig: MediumConfig(),
    minStaffSpacingVertical: 35,
    idealPaddingX: 150,
    idealPaddingY: 75,
  };
  const minProps: SizingProps = {
    minPaddingX: 125,
    minPaddingY: 30,
    idealPhraseConfig: maxProps.minPhraseConfig,
    minPhraseConfig: MinimumConfig(),
    idealPaddingX: maxProps.minPaddingX,
    idealPaddingY: maxProps.minPaddingY,
    minStaffSpacingVertical: 20,
  };
  return { minProps, maxProps };
};

/** How Sight Reading lays a desktop play area out: its line gap and its side padding (CSS px). */
export interface SightReadingLayout {
  /** The distance between two staff lines, at zoom 1 (the lesson zoom multiplies it on screen). */
  lineGap: number;
  /** The space left free on each side of the music. */
  paddingX: number;
}

/** Sight Reading's layout for a desktop play area of `width` x `height` CSS pixels. */
export const sightReadingLayout = (width: number, height: number): SightReadingLayout => {
  if (!(width > 0) || !(height > 0)) {
    throw new Error(`sightReadingLayout: a play area needs a size (got ${width} x ${height})`);
  }
  const { minProps, maxProps } = sightReadingDesktopSizing();
  const layout = optimizeStaffLineHeight(
    [width, height],
    { min: minProps, max: maxProps },
    SIGHT_READING_MIN_ASPECT_RATIO,
    SIGHT_READING_MAX_ASPECT_RATIO,
    false,
  );
  return { lineGap: layout.phraseRenderingConfig.pitchLineGap, paddingX: layout.paddingX };
};

/**
 * The lesson screen's desktop bars (CSS px): a top bar and a bottom bar, the music between them.
 * Sight Reading sizes its staves for the play area they leave in the window.
 */
export const SIGHT_READING_DESKTOP_TOP_BAR_HEIGHT = 160;
export const SIGHT_READING_DESKTOP_BOTTOM_BAR_HEIGHT = 100;

/**
 * Sight Reading's layout in a desktop window of `width` x `height` CSS pixels: the line gap and
 * padding the lesson screen draws with in that window, whatever another screen's own bars are.
 */
export const sightReadingLayoutInWindow = (width: number, height: number): SightReadingLayout =>
  sightReadingLayout(
    width,
    height - SIGHT_READING_DESKTOP_TOP_BAR_HEIGHT - SIGHT_READING_DESKTOP_BOTTOM_BAR_HEIGHT,
  );
